/**
 * ReviewSummary — inline review card rendered on the Embed step.
 *
 * Shows what the user entered in previous steps (org name, page name,
 * domain) with [Edit] buttons. Edits call PATCH endpoints and update
 * wizard state — no duplicate resources, no back-navigation needed.
 */

import { useCallback, useRef, type KeyboardEvent } from 'react'
import { api } from '@/services/api'
import { Button, Input } from '@/components/ui'
import { useInlineEdit } from '@/hooks/useInlineEdit'
import { normalizeDomain, validateDomain } from '@/utils/domain'

export type ReviewSummaryProps = {
  orgId: string
  orgName: string
  canaryId: string
  canaryName: string
  authorizedDomain: string
  authorizedDomainId: string
  onOrgNameUpdated: (name: string) => void
  onCanaryNameUpdated: (name: string) => void
  onDomainUpdated: (domain: string) => void
}

export function ReviewSummary(props: ReviewSummaryProps) {
  const {
    orgId, orgName, canaryId, canaryName,
    authorizedDomain, authorizedDomainId,
    onOrgNameUpdated, onCanaryNameUpdated, onDomainUpdated,
  } = props

  const saveOrgName = useCallback(async (name: string) => {
    await api.updateOrganization(orgId, { name })
    onOrgNameUpdated(name)
  }, [orgId, onOrgNameUpdated])

  const saveCanaryName = useCallback(async (name: string) => {
    await api.updateCanary(canaryId, { name })
    onCanaryNameUpdated(name)
  }, [canaryId, onCanaryNameUpdated])

  const saveDomain = useCallback(async (fqdn: string) => {
    await api.updateAuthorizedDomain(authorizedDomainId, { fqdn })
    onDomainUpdated(fqdn)
  }, [authorizedDomainId, onDomainUpdated])

  const validateName = useCallback((v: string) => {
    const trimmed = v.trim()
    if (!trimmed) return 'This field cannot be empty.'
    if (trimmed.length > 100) return 'Must be 100 characters or fewer.'
    return null
  }, [])

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
      <h3 className="text-sm font-semibold text-gray-700">Review your setup</h3>
      <EditableRow
        label="Organization"
        value={orgName}
        onSave={saveOrgName}
        validate={validateName}
      />
      <EditableRow
        label="Page name"
        value={canaryName}
        onSave={saveCanaryName}
        validate={validateName}
      />
      <EditableRow
        label="Domain"
        value={authorizedDomain}
        onSave={saveDomain}
        normalize={normalizeDomain}
        validate={validateDomain}
      />
    </div>
  )
}

type EditableRowProps = {
  label: string
  value: string
  onSave: (value: string) => Promise<void>
  normalize?: (value: string) => string
  validate?: (value: string) => string | null
}

function EditableRow({ label, value, onSave, normalize, validate }: EditableRowProps) {
  const edit = useInlineEdit({ value, onSave, normalize, validate })
  const inputRef = useRef<HTMLInputElement>(null)

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); void edit.save() }
    if (e.key === 'Escape') edit.cancel()
  }, [edit])

  // Focus input when entering edit mode
  const setRef = useCallback((el: HTMLInputElement | null) => {
    (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el
    if (el) el.focus()
  }, [])

  if (!edit.editing) {
    return (
      <div className="flex items-center justify-between gap-2 min-h-[36px]">
        <div className="flex-1 min-w-0">
          <span className="text-xs text-gray-500">{label}</span>
          <p className="text-sm text-gray-900 truncate">{value}</p>
        </div>
        {edit.success && (
          <span aria-live="polite" className="text-xs text-success-600 shrink-0">Saved!</span>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={edit.startEdit}
          aria-label={`Edit ${label.toLowerCase()}`}
          className="shrink-0"
        >
          Edit
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <span className="text-xs text-gray-500">{label}</span>
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <Input
            ref={setRef}
            name={`edit-${label.toLowerCase().replace(/\s+/g, '-')}`}
            value={edit.draft}
            onChange={(e) => { edit.setDraft(e.target.value) }}
            onKeyDown={handleKeyDown}
            error={edit.error ?? undefined}
            aria-label={`Edit ${label.toLowerCase()}`}
            className="w-full"
          />
        </div>
        <Button
          size="sm"
          onClick={() => void edit.save()}
          isLoading={edit.saving}
          className="shrink-0"
        >
          Save
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={edit.cancel}
          disabled={edit.saving}
          className="shrink-0"
        >
          Cancel
        </Button>
      </div>
    </div>
  )
}
